import {
  BEDSIDE_KEY,
  COUNTER_KEY,
  CURBSIDE_KEY,
  OTC_DELIVERY_KEY,
  PROMOTED_PRODUCTS,
  isComingSoonFeature,
  resolvePromotedEntitlement,
} from './productLicensing';

export const DEFAULT_PROMINENT_SOLUTION_KEYS = [
  COUNTER_KEY,
  CURBSIDE_KEY,
  BEDSIDE_KEY,
  OTC_DELIVERY_KEY,
];

export const TOP_LEVEL_PRODUCT_CAP = 4;
export const PINNED_NAV_CAP = 8;
export const FORCE_PROMOTE_KEYS = [OTC_DELIVERY_KEY];
export const NAV_PREFS_STORAGE_KEY = 'ilocalbox.navPreferences.v1';

export const getVisiblePromotedProducts = (showConsole) =>
  PROMOTED_PRODUCTS.filter((product) =>
    product.requiresConsole ? !!showConsole : true
  );

export const rankPromotedProducts = (products, entitlements) =>
  (products || []).map((product) => ({
    product,
    entitlement: resolvePromotedEntitlement(entitlements, product.feature_key),
  }));

const uniquePush = (list, used, item) => {
  const key = item && item.product && item.product.feature_key;
  if (!key || used.has(key)) {
    return;
  }
  used.add(key);
  list.push(item);
};

export const splitPromotedNavItems = (
  products,
  entitlements,
  options = {}
) => {
  const forceKeys = options.forcePromoteKeys || FORCE_PROMOTE_KEYS;
  const explicitPins = Array.isArray(options.pinnedKeys) ? options.pinnedKeys : [];
  const hasExplicitPins = explicitPins.length > 0;
  const ranked = rankPromotedProducts(products, entitlements);
  const used = new Set();
  const top = [];

  const pinKey = (key, allowComingSoon) => {
    if (top.length >= PINNED_NAV_CAP) {
      return;
    }
    if (!allowComingSoon && isComingSoonFeature(key)) {
      return;
    }
    const item = ranked.find((entry) => entry.product.feature_key === key);
    uniquePush(top, used, item);
  };

  if (hasExplicitPins) {
    explicitPins.forEach((key) => pinKey(key, true));
  } else {
    DEFAULT_PROMINENT_SOLUTION_KEYS.forEach((key) => pinKey(key, false));
  }
  forceKeys.forEach((key) => pinKey(key, true));

  return {
    top,
    more: ranked.filter((entry) => !used.has(entry.product.feature_key)),
  };
};

export const defaultPinnedProductKeys = (products, entitlements) =>
  splitPromotedNavItems(products, entitlements).top.map(
    (entry) => entry.product.feature_key
  );

const uniqueStrings = (list) => {
  const out = [];
  (list || []).forEach((item) => {
    if (!item) {
      return;
    }
    const value = String(item);
    if (out.indexOf(value) === -1) {
      out.push(value);
    }
  });
  return out;
};

const readPrefsStore = () => {
  const raw = localStorage.getItem(NAV_PREFS_STORAGE_KEY);
  const parsed = raw ? JSON.parse(raw) : {};
  return parsed && typeof parsed === 'object' ? parsed : {};
};

export const getPrefsUserKeys = () => {
  const keys = [];
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    if (user && user.username) {
      keys.push(user.username);
    }
    const payload =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.payload;
    if (payload && payload.email) {
      keys.push(payload.email);
    }
    if (payload && payload.sub) {
      keys.push(payload.sub);
    }
    if (payload && payload['cognito:username']) {
      keys.push(payload['cognito:username']);
    }
  } catch (_err) {
    // ignore session parse errors and fall back to remaining keys
  }
  try {
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      keys.push(userId);
    }
  } catch (_err) {
    // ignore
  }
  keys.push('anonymous');
  return uniqueStrings(keys);
};

export const normalizeNavPreferences = (raw) => {
  const source =
    raw && raw.nav_preferences && typeof raw.nav_preferences === 'object'
      ? raw.nav_preferences
      : raw;
  if (!source || typeof source !== 'object') {
    return { pinnedKeys: null, tourSeen: false, hasPrefs: false };
  }
  const hasPrefs =
    source.hasPrefs === true ||
    Array.isArray(source.pinnedKeys) ||
    source.tourSeen === true;
  return {
    pinnedKeys: Array.isArray(source.pinnedKeys) ? source.pinnedKeys : null,
    tourSeen: source.tourSeen === true,
    hasPrefs,
  };
};

export const loadNavPreferences = () => {
  try {
    const parsed = readPrefsStore();
    const keys = getPrefsUserKeys();
    let pinnedKeys = null;
    let tourSeen = false;
    for (let i = 0; i < keys.length; i++) {
      const prefs = parsed[keys[i]];
      if (!prefs) {
        continue;
      }
      if (!pinnedKeys && Array.isArray(prefs.pinnedKeys)) {
        pinnedKeys = prefs.pinnedKeys;
      }
      if (prefs.tourSeen === true) {
        tourSeen = true;
      }
    }
    return { pinnedKeys, tourSeen };
  } catch (_err) {
    return { pinnedKeys: null, tourSeen: false };
  }
};

export const saveNavPreferences = (nextPrefs) => {
  try {
    const parsed = readPrefsStore();
    const record = {
      pinnedKeys: Array.isArray(nextPrefs.pinnedKeys)
        ? nextPrefs.pinnedKeys
        : null,
      tourSeen: nextPrefs.tourSeen === true,
    };
    const keys = getPrefsUserKeys();
    keys.forEach((key) => {
      if (key === 'anonymous' && keys.length > 1) {
        return;
      }
      parsed[key] = record;
    });
    localStorage.setItem(NAV_PREFS_STORAGE_KEY, JSON.stringify(parsed));
  } catch (_err) {
    return;
  }
};

const getAuthToken = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    return (
      (user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken) ||
      ''
    );
  } catch (_err) {
    return '';
  }
};

export const fetchNavPreferencesFromApi = () => {
  const token = getAuthToken();
  const base = process.env.REACT_APP_BASEURL;
  if (!token || !base) {
    return Promise.resolve(null);
  }
  return fetch(base + 'user/nav-preferences', {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
  })
    .then((response) => {
      if (!response.ok) {
        return null;
      }
      return response.json().then(normalizeNavPreferences).catch(() => null);
    })
    .catch(() => null);
};

export const persistNavPreferencesToApi = (prefs) => {
  const token = getAuthToken();
  const base = process.env.REACT_APP_BASEURL;
  if (!token || !base) {
    return Promise.resolve(false);
  }
  return fetch(base + 'user/nav-preferences', {
    method: 'PATCH',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      pinnedKeys: Array.isArray(prefs.pinnedKeys) ? prefs.pinnedKeys : null,
      tourSeen: prefs.tourSeen === true,
    }),
  })
    .then((response) => response.ok)
    .catch(() => false);
};



// WEBPACK FOOTER //
// ./src/utils/productNav.js